// Enhanced Authentication Service for web-app-request
// Includes refresh token functionality, Safari compatibility, and enhanced error handling

import { API_ENDPOINTS } from "./config.js";

// Safari detection utility
const isSafari = () => {
  const userAgent = navigator.userAgent;
  return (
    /^((?!chrome|android).)*safari/i.test(userAgent) ||
    /iPad|iPhone|iPod/.test(userAgent)
  );
};

// Cross-browser persistent storage with Safari compatibility
const persistentStorage = {
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      if (isSafari()) {
        sessionStorage.setItem(key, value);
        localStorage.setItem(`${key}_safari_flag`, Date.now().toString());
      }
    } catch (error) {
      console.warn("Storage failed:", error);
      sessionStorage.setItem(key, value);
    }
  },

  getItem: (key) => {
    try {
      let value = localStorage.getItem(key);
      if (!value && isSafari()) {
        value = sessionStorage.getItem(key);
        if (value) {
          localStorage.setItem(key, value);
        }
      }
      return value;
    } catch (error) {
      console.warn("Storage read failed:", error);
      return sessionStorage.getItem(key);
    }
  },

  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
      if (isSafari()) {
        localStorage.removeItem(`${key}_safari_flag`);
      }
    } catch (error) {
      console.warn("Storage removal failed:", error);
    }
  },
};

/**
 * Enhanced Authentication Service Class
 */
export class AuthService {
  static _refreshPromise = null;
  static _tokenExpiryTime = null;
  static _sessionCheckPromise = null;
  static _sessionRegisterPromise = null;

  static getAccessToken() {
    return persistentStorage.getItem("tringAuthToken");
  }

  static setAccessToken(token, expiresIn = 3600) {
    if (token) {
      persistentStorage.setItem("tringAuthToken", token);
      this._tokenExpiryTime = Date.now() + expiresIn * 1000;
      persistentStorage.setItem(
        "tringAuthToken_expiry",
        this._tokenExpiryTime.toString(),
      );

      if (isSafari()) {
        const safariExpiry = Date.now() + expiresIn * 1000 * 2;
        persistentStorage.setItem(
          "tringAuthToken_expiry_safari",
          safariExpiry.toString(),
        );
      }
    } else {
      persistentStorage.removeItem("tringAuthToken");
      persistentStorage.removeItem("tringAuthToken_expiry");
      persistentStorage.removeItem("tringAuthToken_expiry_safari");
      this._tokenExpiryTime = null;
    }
  }

  static getRefreshToken() {
    return persistentStorage.getItem("tringAuthToken_refresh");
  }

  static setRefreshToken(token) {
    if (token) {
      persistentStorage.setItem("tringAuthToken_refresh", token);
    } else {
      persistentStorage.removeItem("tringAuthToken_refresh");
    }
  }

  static isTokenExpired() {
    const expiryTime = persistentStorage.getItem("tringAuthToken_expiry");
    if (!expiryTime) return true;

    const expiry = parseInt(expiryTime, 10);
    this._tokenExpiryTime = expiry;

    if (isSafari()) {
      const safariFlag = persistentStorage.getItem(
        "tringAuthToken_safari_flag",
      );
      const safariExpiry = persistentStorage.getItem(
        "tringAuthToken_expiry_safari",
      );

      if (!safariFlag && safariExpiry) {
        return Date.now() >= parseInt(safariExpiry, 10);
      }
    }

    return Date.now() >= expiry;
  }

  static clearTokens() {
    // Clear main token and related data
    persistentStorage.removeItem("tringAuthToken");
    persistentStorage.removeItem("tringAuthToken_expiry");
    persistentStorage.removeItem("tringAuthToken_expiry_safari");
    persistentStorage.removeItem("tringAuthToken_refresh");
    persistentStorage.removeItem("tringAuthToken_safari_flag");

    // Clear user data
    persistentStorage.removeItem("userId");
    persistentStorage.removeItem("artistId");
    persistentStorage.removeItem("businessId");

    this._tokenExpiryTime = null;
    this._refreshPromise = null;
    this._sessionCheckPromise = null;
    this._sessionRegisterPromise = null;
  }

  // Token refresh with rotation
  static async refreshToken() {
    if (this._refreshPromise) {
      return await this._refreshPromise;
    }

    this._refreshPromise = (async () => {
      try {
        const currentRefreshToken = this.getRefreshToken();
        if (!currentRefreshToken) {
          throw new Error("No refresh token available");
        }

        const response = await fetch(API_ENDPOINTS.AUTH_REFRESH, {
          method: "POST",
          headers: {
            "X-Platform": "browser",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          this.clearTokens();
          throw new Error(
            `Refresh failed: ${errorData.message || response.statusText}`,
          );
        }

        const data = await response.json();
        this.setAccessToken(data.accessToken, data.expiresIn);
        if (data.refreshToken) {
          this.setRefreshToken(data.refreshToken);
        }

        return data.accessToken;
      } catch (error) {
        this.clearTokens();
        throw error;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return await this._refreshPromise;
  }
  // Handle verify OTP response - replaces session registration
  static async handleVerifyOtpResponse(verifyResponse) {
    if (this._sessionRegisterPromise) {
      return await this._sessionRegisterPromise;
    }

    this._sessionRegisterPromise = (async () => {
      try {
        // Extract tokens and user data from verify OTP response
        const { tokens, id, isUserRegistered, role } = verifyResponse;

        if (!tokens?.accessToken) {
          throw new Error("No access token received from verify OTP response");
        }

        // Set tokens using AuthService
        this.setAccessToken(tokens.accessToken, tokens.expiresIn);
        if (tokens?.refreshToken) {
          this.setRefreshToken(tokens.refreshToken);
        }

        // Store user data
        persistentStorage.setItem("userId", id);
        persistentStorage.setItem("PerformerId", id);
        
        // Store role information
        if (role) {
          persistentStorage.setItem("userRole", JSON.stringify(role));
          persistentStorage.setItem("isWaiter", role.includes("Waiter"));
          persistentStorage.setItem("isKj", role.includes("KJ"));
        }

        return {
          userId: id,
          accessToken: tokens.accessToken,
          isUserRegistered: isUserRegistered,
          role: role,
          userData: verifyResponse,
        };
      } catch (error) {
        console.error("Error handling verify OTP response:", error);
        throw error;
      }
    })().finally(() => {
      this._sessionRegisterPromise = null;
    });

    return await this._sessionRegisterPromise;
  }

  // Legacy session registration method - kept for backward compatibility but deprecated
  static async registerSession(idToken) {
    console.warn("registerSession is deprecated. Use handleVerifyOtpResponse instead.");
    throw new Error("Session registration is no longer supported. Use verify OTP API instead.");
  }

  // Legacy compatibility methods
  static getAuthToken() {
    return this.getAccessToken();
  }

  static isAuthenticated() {
    return !!this.getAccessToken();
  }

  static setAuthToken(token, type = "auth") {
    // Ignore type parameter since we only use one token
    this.setAccessToken(token, 3600);
  }

  static clearAuthTokens() {
    this.clearTokens();
  }
}

/**
 * Enhanced fetch function with automatic token refresh and retry
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export const fetchWithAuth = async (url, options = {}) => {
  const requestId = Math.random().toString(36).substr(2, 9);

  // Check if token is expired and refresh if needed
  if (AuthService.isTokenExpired()) {
    try {
      await AuthService.refreshToken();
    } catch (error) {
      console.warn(`[${requestId}] Token refresh failed:`, error);
      // Don't throw here, let the request proceed and handle 401 below
    }
  }

  const authToken = AuthService.getAccessToken();

  if (!authToken) {
    console.warn(`[${requestId}] No access token available`);
  }

  // Prepare headers
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  // Add authorization header if token exists
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle token refresh if needed (401 response)
  if (response.status === 401 && authToken) {
    try {
      console.log(`[${requestId}] 401 received, attempting token refresh`);
      const newToken = await AuthService.refreshToken();

      const retryOptions = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
          "x-platform": "browser",
          "Content-Type": "application/json",
        },
      };

      response = await fetch(url, retryOptions);
      console.log(`[${requestId}] Request retried successfully after refresh`);
    } catch (refreshError) {
      console.error(`[${requestId}] Token refresh failed:`, refreshError);
      AuthService.clearTokens();

      // Redirect to login page if on web
      if (typeof window !== "undefined" && window.location) {
        if (
          window.location.pathname !== "/login" &&
          window.location.pathname !== "/"
        ) {
          window.location.href = "/login";
        }
      }

      throw refreshError;
    }
  }

  return response;
};

// Legacy exports for backward compatibility
export const getAuthToken = () => AuthService.getAuthToken();
export const isAuthenticated = () => AuthService.isAuthenticated();
export const clearAuthTokens = () => AuthService.clearAuthTokens();
export const setAuthToken = (token, type) =>
  AuthService.setAuthToken(token, type);
